import { CQAppConstant } from "app-constants";
import { SFConnection } from "common/sf-connector";
import DefaultStorage from "common/storage";
import { IPastRun, TaskEntry } from "interfaces/IHistory.interface";
import { getInstalledPackagesHistoryKey } from "common/module.utils";

const MAX_HISTORY = 10;

export class TaskEngine {
    tasksToRun : TaskEntry[];
    listeners: (() => void)[];
    currentModule: string = CQAppConstant.MODULE_TYPES.UPDATES;

    constructor() {
        this.tasksToRun = [];
        this.listeners = [];
    }
    
    getHistory() : Promise<IPastRun[]> {
        let pastRuns : IPastRun[] = this.getHistorySync();
        return Promise.resolve(pastRuns);
    }

    addListener (listener: () => void) {
        this.listeners.push(listener);
    }

    removeListener (listener: () => void) {
        let listenerIndex = this.listeners.indexOf(listener);
        if(listenerIndex !== -1) {
            this.listeners.splice(listenerIndex, 1);
        }
    }

    /**
     * Restores persisted state and invokes all registered listeners.
     *
     * Awaits completion of {@link DefaultStorage.restore} to ensure the most
     * up-to-date persisted data is available before notifying listeners.
     * Once restoration completes, each registered listener is invoked
     * synchronously in registration order.
     *
     * Any exception thrown by a listener is caught and logged so that
     * execution of subsequent listeners is not interrupted.
     *
     * @async
     * @returns {Promise<void>} A promise that resolves after all listeners
     * have been invoked.
     * @param {boolean} skipApiCall - If true, skips the DefaultStorage.restore() call
    */
    async invokeListeners(skipApiCall: boolean = false) {
        if (!skipApiCall) {
            await DefaultStorage.restore();
        }
        this.listeners.forEach(listener => {
            try {
                listener();
            } catch (ex) {
                console.error('Exception occurred in listener', listener);
            }
        });
    }

    /**
     * Retrieves the history of task runs for the current Salesforce organization synchronously.
     *
     * Reads the stored history from DefaultStorage using the current org ID and the key 'cq-tasks'.
     * Returns an array of IPastRun objects representing previous task runs.
     *
     * @returns {IPastRun[]} An array of past task runs.
     */
    getHistorySync() : IPastRun[] {
        return JSON.parse(DefaultStorage.readData(CQAppConstant.CQ_DOCUMENT_LOG.CQ_TASKS) || '[]');
    }

    /**
     * Reads the history of installed packages for the current organization.
     *
     * This method retrieves data from the default storage associated with the
     * current Salesforce organization ID. If no data is found, it defaults to an
     * empty array. The data is parsed and returned as a JSON object.
     *
     * @returns {Promise<any>} A promise that resolves to the parsed history of
     * installed packages, or an empty array if no data is available.
     */
    async readInstalledPackagesHistory() {
        let data = await (DefaultStorage.readData(getInstalledPackagesHistoryKey(this.currentModule) || '') || null);
        if (data == null) {
            return await (DefaultStorage.readData(CQAppConstant.CQ.HISTORY) || null);
        }
        return data;
    }
}


const DefaultTaskEngine = new TaskEngine();

export default DefaultTaskEngine;